import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarDays, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { appointmentManage, appointmentReschedule, appointmentCancel, receptionistAvailability } from "@/lib/api";

// Public, token-gated self-service page reached from the caller's confirmation email: /r/:aid?t=<token>
export default function ManageBooking() {
  const { aid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [data, setData] = useState(undefined); // undefined=loading, null=error
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    appointmentManage(aid, token).then(setData).catch(() => setData(null));
  }, [aid, token]);

  const loadSlots = async (d) => {
    setDate(d); setTime(""); setSlots(null);
    if (!d || !data) return;
    try { const a = await receptionistAvailability(data.receptionist_id, d); setSlots(a.slots || []); }
    catch { setSlots([]); }
  };

  const reschedule = async () => {
    setErr(""); if (!date || !time) { setErr("Pick a new date and time."); return; }
    setBusy(true);
    try {
      const r = await appointmentReschedule(aid, token, date, time);
      setMsg(`Your appointment is now ${r.date} at ${r.time}.`);
      setData((d) => ({ ...d, appointment: { ...d.appointment, date: r.date, time: r.time, status: "requested" } }));
    } catch (e) { setErr(e?.response?.data?.detail || "Could not reschedule."); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this appointment?")) return;
    setBusy(true); setErr("");
    try { await appointmentCancel(aid, token); setData((d) => ({ ...d, appointment: { ...d.appointment, status: "cancelled" } })); setMsg("Your appointment has been cancelled."); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not cancel."); }
    finally { setBusy(false); }
  };

  if (data === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (data === null) return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="text-center max-w-sm" data-testid="manage-invalid">
        <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-slate-900">Link not valid</h1>
        <p className="text-slate-500 mt-1 text-sm">This reschedule link is invalid or has expired. Please contact the business directly.</p>
      </div>
    </div>
  );

  const a = data.appointment;
  const cancelled = a.status === "cancelled";

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="manage-booking">
        <div className="flex items-center gap-2 text-blue-600 mb-1"><CalendarDays className="w-5 h-5" /><span className="font-semibold">{data.business_name}</span></div>
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Manage your booking</h1>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm mb-5" data-testid="current-booking">
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Name</span><strong>{a.name}</strong></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Service</span><strong>{a.service || "Appointment"}</strong></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-500">When</span><strong>{a.date} at {a.time}</strong></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Status</span><strong className={cancelled ? "text-red-600" : "text-emerald-600"}>{a.status}</strong></div>
        </div>

        {msg && <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 text-sm mb-4" data-testid="manage-msg"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}

        {!cancelled && (
          <>
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Reschedule</label>
              <div className="flex gap-2">
                <input type="date" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" value={date} onChange={(e) => loadSlots(e.target.value)} data-testid="reschedule-date" />
                <select className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" value={time} onChange={(e) => setTime(e.target.value)} disabled={!date} data-testid="reschedule-time">
                  {!date && <option value="">Pick a date</option>}
                  {date && slots === null && <option value="">Loading…</option>}
                  {date && slots !== null && slots.length === 0 && <option value="">No open slots</option>}
                  {date && slots !== null && slots.length > 0 && <option value="">Choose a time</option>}
                  {(slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {err && <div className="text-xs text-red-600 mt-2" data-testid="manage-err">{err}</div>}
            <div className="flex gap-2 mt-4">
              <button onClick={reschedule} disabled={busy} data-testid="reschedule-btn" className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{busy ? "Saving…" : "Reschedule"}</button>
              <button onClick={cancel} disabled={busy} data-testid="cancel-btn" className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-600 hover:text-red-600 hover:border-red-300">Cancel booking</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
